import React, { useState, useEffect } from 'react'
import { Card, CardContent } from '../../ui/card'
import { Button } from '../../ui/button'
import { supabase } from '../../../lib/supabase'
import { toast } from 'sonner'
import { Input } from '../../ui/input'
import { Loader2, ArrowRight } from 'lucide-react'
import { debug } from '../../../lib/debug'
import { cn } from '../../../lib/utils'

interface Role {
  id: string
  name: string
  function: string
  level: string
}

interface RoleSelectorProps {
  onRoleSelect: (roleId: string | null, customRole: string | null) => void
  onComplete: () => void
  savedRole?: { roleId: string | null; customRole: string | null }
}

export function RoleSelector({ onRoleSelect, onComplete, savedRole }: RoleSelectorProps) {
  const [roles, setRoles] = useState<Role[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [selectedRole, setSelectedRole] = useState<string | null>(savedRole?.roleId || null)
  const [customRole, setCustomRole] = useState(savedRole?.customRole || '')
  const [showCustomInput, setShowCustomInput] = useState(!!savedRole?.customRole)
  const [selectedLevel, setSelectedLevel] = useState<string>(() => {
    if (savedRole?.roleId) {
      const role = roles.find(r => r.id === savedRole.roleId)
      return role?.level || 'C-Suite'
    }
    if (savedRole?.customRole) return 'Other'
    return 'C-Suite'
  })

  useEffect(() => {
    loadRoles()
  }, [])

  const loadRoles = async () => {
    try {
      const { data, error } = await supabase
        .from('roles')
        .select('*')
        .eq('is_active', true)
        .order('sort_order')

      if (error) throw error
      
      debug('ROLE_SELECTION', 'Roles loaded:', data.length)
      setRoles(data)

      if (savedRole?.roleId) {
        const role = data.find(r => r.id === savedRole.roleId)
        if (role) {
          setSelectedLevel(role.level)
        }
      }
    } catch (error) {
      console.error('Error loading roles:', error)
      toast.error('Failed to load roles')
    } finally {
      setIsLoading(false)
    }
  }

  const handleRoleSelect = (roleId: string) => {
    const selectedRole = roles.find(r => r.id === roleId)
    
    if (selectedRole?.name === 'Other') {
      setSelectedRole(roleId)
      setShowCustomInput(true)
    } else {
      setSelectedRole(roleId)
      setShowCustomInput(false)
      setCustomRole('')
      onRoleSelect(roleId, null)
    }
  }

  const handleCustomRoleChange = (value: string) => {
    setCustomRole(value)
    if (selectedRole) {
      onRoleSelect(selectedRole, value)
    }
  }

  const handleNext = () => {
    if (selectedRole) {
      if (showCustomInput && !customRole) {
        toast.error('Please enter your role title')
        return
      }
      onComplete()
    } else {
      toast.error('Please select a role')
    }
  }

  const levels = ['C-Suite', 'VP', 'Director', 'Manager/Lead', 'Other']
  const rolesByLevel = roles.reduce((acc, role) => {
    if (!acc[role.level]) {
      acc[role.level] = []
    }
    acc[role.level].push(role)
    return acc
  }, {} as Record<string, Role[]>)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <Card>
      <CardContent className="p-6 space-y-8">
        <div className="flex items-center gap-4">
          <label htmlFor="role-level" className="text-sm font-medium whitespace-nowrap">
            Select Role Level:
          </label>
          <select
            id="role-level"
            value={selectedLevel}
            onChange={(e) => setSelectedLevel(e.target.value)}
            className="flex-1 max-w-xs h-10 px-3 rounded-md border border-input bg-background text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            {levels.map(level => (
              <option key={level} value={level}>{level}</option>
            ))}
          </select>
        </div>

        <div className="space-y-4">
          <label className="text-sm font-medium block">Select Role</label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {rolesByLevel[selectedLevel]?.map((role) => {
              const displayName = role.name === 'Other' ? role.name : 
                role.level === 'Manager' ? `${role.level} (${role.function})` : role.name

              return (
                <Button
                  key={role.id}
                  type="button"
                  variant={selectedRole === role.id ? 'default' : 'outline'}
                  className={cn(
                    "justify-start h-auto py-3 px-4 w-full text-left",
                    "transition-colors",
                    "touch-none select-none",
                    selectedRole === role.id ? [
                      "bg-primary text-primary-foreground",
                      "md:hover:bg-primary/90 md:active:bg-primary/80",
                      "dark:bg-primary dark:text-primary-foreground",
                      "dark:md:hover:bg-primary/90 dark:md:active:bg-primary/80"
                    ] : [
                      "bg-background text-foreground border border-border",
                      "md:hover:bg-primary/20 md:hover:border-primary/50",
                      "md:active:bg-primary/30",
                      "dark:bg-background dark:text-foreground dark:border-border/40",
                      "dark:md:hover:bg-primary/20 dark:md:hover:border-primary/50",
                      "dark:md:active:bg-primary/30"
                    ]
                  )}
                  onClick={() => handleRoleSelect(role.id)}
                >
                  <div>
                    <div className="text-sm font-medium">
                      {displayName}
                    </div>
                    <div className={cn(
                      "text-xs",
                      selectedRole === role.id 
                        ? "text-primary-foreground/80" 
                        : "text-muted-foreground"
                    )}>
                      {role.function}
                    </div>
                  </div>
                </Button>
              )
            })}
          </div>
        </div>

        {showCustomInput && (
          <div className="space-y-2 pt-4 border-t">
            <label className="text-sm font-medium">
              Enter your role title
            </label>
            <Input
              value={customRole}
              onChange={(e) => handleCustomRoleChange(e.target.value)}
              placeholder="Enter your role title"
              className="max-w-md"
            />
          </div>
        )}

        <div className="flex justify-end pt-4">
          <Button 
            onClick={handleNext}
            disabled={!selectedRole || (showCustomInput && !customRole)}
            className="w-[140px] flex items-center justify-center gap-2"
          >
            Next <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
